import { Component, Injectable } from '@angular/core';
import {
  AlertController,
  LoadingController,
  ModalController,
  PopoverController,
  ToastController,
} from '@ionic/angular';
import { KeyboardComponent } from '@viso/core/ui/keyboard';
import { ImageViewerComponent } from '@viso/core/ui/image-viewer';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { ToastOptions } from '@ionic/angular';
import { Clipboard } from '@capacitor/clipboard';

// TODO: later it will be provider based
const LOADING_OPTIONS = {
  spinner: null,
  translucent: true,
};

// TODO: later it will be provider based
const TOAST_DEFAULT: ToastOptions = {
  duration: 3000,
  buttons: [],
  mode: 'md',
};
@Injectable({
  providedIn: 'any',
})
export class IonicUtilityService {
  private loadingRef: HTMLIonLoadingElement | null = null;
  viewerRef;
  toast: any;
  constructor(
    private loading: LoadingController,
    private toastr: ToastController,
    private alert: AlertController,
    readonly modalController: ModalController,
    private alertController: AlertController,
    private popoverController: PopoverController,
    private router: Router
  ) {}

  writeToClipboard = async (string) => {
    await Clipboard.write({
      string
    });
    this.showToast('Copied.');
  };

  async showLoading(settings = { spinner: 'crescent' }) {
    if (this.loadingRef) {
      await this.hideLoading();
    }
    const options = { ...LOADING_OPTIONS, ...settings };
    const loading = await this.loading.create({
      spinner: 'crescent',
      translucent: true,
    });
    await loading.present();
    this.loadingRef = loading;
    return loading;
  }

  async hideLoading() {
    if (this.loadingRef) {
      await this.loadingRef.dismiss(); // Await the dismissal
      this.loadingRef = null;
    }
  }

  get isNative() {
    return Capacitor.getPlatform() !== 'web';
  }

  triggerOfflineToast() {
    // HACK: fix toast not presented when offline, due to lazy loading the toast controller.
    // Can be removed once #17450 is resolved: https://github.com/ionic-team/ionic/issues/17450
    this.toastr.create({ animated: false }).then((t) => {
      t.present();
      t.dismiss();
    });
  }

  triggerAlert() {
    this.alert
      .create({
        buttons: [],
        animated: false,
      })
      .then((t) => {
        t.present();
        t.dismiss();
      });
  }

  async showToast(message, options = {}) {
    const toast = await this.toastr.create({
      message,
      ...TOAST_DEFAULT,
      ...options,
    });
    toast.present();
    return toast;
  }

  async showAlert(header, subHeader, message, cssClass, buttons, backdropDismiss=true) {
    const alert = await this.alert.create({
      cssClass,
      header,
      subHeader,
      message,
      buttons,
    });
    await alert.present();
    return alert;
  }

  // TODO: Implement if needed a Storage
  storage() {}

  async triggerModal() {
    await this.modalController.create({
      component: VisoBlankModalComponent,
    });
  }

  async showModal(component, props, cssClass = '') {
    const modal = await this.modalController.create({
      component,
      componentProps: {
        ...props,
      },
      cssClass,
    });
  
    // Function to handle popstate event
    const handlePopState = () => {
      modal.dismiss();
    };
  
    // Add popstate listener
    window.addEventListener('popstate', handlePopState);
  
    // Handle modal dismissal
    modal.onDidDismiss().then(() => {
      // Remove the popstate listener when the modal is dismissed
      window.removeEventListener('popstate', handlePopState);
    });
  
    // Push a new history state for the modal
    history.pushState(null, '');
  
    // Present the modal
    await modal.present();
    return modal;
  }

    async displayModal(component, props, callback, cssClass = '') {
      const modal = await this.modalController.create({
        component,
        componentProps: {
          ...props,
        },
        cssClass,
      });
    
      // Add an entry to the browser history stack
      const stateKey = `modal-${Date.now()}`;
      history.pushState({ key: stateKey }, '');
    
      const handlePopState = () => {
        // Dismiss the modal when the back button is pressed
        modal.dismiss();
      };
    
      // Add a listener for the popstate event
      window.addEventListener('popstate', handlePopState);
    
      modal.onDidDismiss().then(() => {
        // Remove the popstate listener and restore history
        window.removeEventListener('popstate', handlePopState);
    
        // Check if the modal added a state and needs to remove it
        if (history.state?.key === stateKey) {
          history.back(); // Go back one step to restore the original history state
        }
    
        // Call the callback if provided
        if (callback) {
          callback();
        }
      });
    
      await modal.present();
      return modal;
    }
    

  async confirmation(message, buttonLabel: {ok: string, cancel: string}, callback) {
    const alert = await this.alertController.create({
      header: 'Confirm!',
      message,
      buttons: [
        {
          text: buttonLabel.cancel,
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            callback(false);
          },
        },
        {
          text: buttonLabel.ok,
          handler: () => {
            callback(true);
          },
        },
      ],
    });
    await alert.present();
  }

  async presentPopover(ev: any, popoverComponent, menuOptions, dismissFn) {
    const popover = await this.popoverController.create({
      component: popoverComponent,
      event: ev,
      translucent: true,
      componentProps: { menuOptions },
    });
  
    const handlePopState = () => {
      popover.dismiss();
    };
  
    // Add popstate listener
    window.addEventListener('popstate', handlePopState);
  
    popover.onDidDismiss().then((res) => {
      // Remove the popstate listener when the popover is dismissed
      window.removeEventListener('popstate', handlePopState);
  
      // Call the dismiss function
      if (dismissFn) {
        dismissFn(res);
      }
    });
  
    // Push state to history for back button handling
    history.pushState(null, '');
  
    return await popover.present();
  }
  
  async showPopover(ev: any, popoverComponent, menuOptions, dismissFn) {
    const popover = await this.popoverController.create({
      event: ev,
      component: popoverComponent,
      translucent: true,
      componentProps: { menuOptions },
      cssClass: 'custom-popover-class',
    });
  
    // Function to handle popstate event
    const handlePopState = () => {
      popover.dismiss();
    };
  
    // Add popstate listener
    window.addEventListener('popstate', handlePopState);
  
    // Handle popover dismissal
    popover.onDidDismiss().then((res) => {
      // Remove the popstate listener when the popover is dismissed
      window.removeEventListener('popstate', handlePopState);
  
      // Invoke the dismiss function, if provided
      if (dismissFn) {
        dismissFn(res);
      }
    });
  
    // Push a new history state for the popover
    history.pushState(null, '');
  
    // Present the popover
    return await popover.present();
  }

  async openKeyboard(componentProps: {
    type?: string,
    placeholder?: string,
    value?: string,
    isDecimal?: boolean,
    previewAvailable: boolean,
    maxlength?: number,
  }, callback) {
    const keyboardAlert = await this.modalController.create({
      component: KeyboardComponent,
      cssClass: 'whitebackground',
      componentProps: {
        ...componentProps,
        callback
      }
    });

    keyboardAlert.onDidDismiss()
    .then(res => {
      if(res.role === 'done') {
        callback(res.data);
      }
    });

    return await keyboardAlert.present();
  }

  async hideKeyboard() {
    this.modalController.dismiss();
  }

  async openViewer(componentProps: {images: any, title: string, text: string, startFrom: number, close?: Function}) {
    this.viewerRef = await this.modalController.create({
      component: ImageViewerComponent,
      componentProps,
      cssClass: 'ion-img-viewer',
    });
    return await this.viewerRef.present();
  }
  
  async liveToast(header:string, message: string, url?: string) {

    if (document.hidden) {
      console.warn('Skipping toast - tab is in background');
      return;
    }

    try {
      //this.toast.dismiss();
      // if you received new notification previous notification will automatically removed
      if (this.toast) {
        await this.toast.dismiss();
      }
    } catch (e) {
      console.warn('Error dismissing previous toast', e);
    }
    
    this.toast = await this.toastr.create({
      header,
      message,
      duration:3000,
      position: 'top',
      layout:'stacked',
      buttons: [
        {
          text: 'View',
          handler: () => {
            this.router.navigateByUrl(url);
          }
        },
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
          }
        }
      ],
      mode: 'md'
    });

    // Clear reference after dismissal to allow re-showing same toast
    this.toast.onDidDismiss().then(() => {
     this.toast = null;
    });

    await this.toast.present();
  }
}

@Component({
  selector: 'viso-blank-modal',
  template: ``,
})
export class VisoBlankModalComponent {}
